import { createContext, useContext, useEffect } from 'react';

import type { ShareRevocationEvent } from './correspondence-share-access';

export type { ShareRevocationEvent };

type CorrespondenceShareEventKind = 'granted' | 'revoked';

export type CorrespondenceShareListener = (
  event: ShareRevocationEvent,
  kind: CorrespondenceShareEventKind,
) => void;

export interface ShareAccessRealtimeContextValue {
  readonly subscribeToCorrespondenceEvents: (listener: CorrespondenceShareListener) => () => void;
}

export const ShareAccessRealtimeContext = createContext<ShareAccessRealtimeContextValue | null>(
  null,
);

interface CorrespondenceShareEventHandlers {
  readonly onGranted?: (event: ShareRevocationEvent) => void;
  readonly onRevoked?: (event: ShareRevocationEvent) => void;
}

/**
 * Observe correspondence share grant/revoke events from the app-root provider
 * without opening a second subscription. Used by the email detail panel to drive
 * its local "blank the open thread on revoke" state.
 *
 * Pass STABLE handlers (`useCallback`): the subscription effect re-registers the
 * listener whenever `onGranted`/`onRevoked` change identity. That is benign
 * (a synchronous Set add/remove, no channel involvement) but avoids needless
 * churn.
 */
export function useCorrespondenceShareEvents(handlers: CorrespondenceShareEventHandlers): void {
  const context = useContext(ShareAccessRealtimeContext);
  const { onGranted, onRevoked } = handlers;

  useEffect(() => {
    if (!context) {
      return;
    }
    return context.subscribeToCorrespondenceEvents((event, kind) => {
      if (kind === 'granted') {
        onGranted?.(event);
      } else {
        onRevoked?.(event);
      }
    });
  }, [context, onGranted, onRevoked]);
}
